import { BroadcastChannel } from 'broadcast-channel';
import dayjs, { type Dayjs } from 'dayjs';
import { isNil } from 'lodash';
import { nanoid } from 'nanoid';
import { useMemo, useEffect, useCallback, useRef, useState } from 'react';

const storageName = 'appvantage::persistent-data';

const storageObject = window.sessionStorage;

type PersistentData = { payload: any; expiresAt: string; key: string; sessionId?: string };

const PersistentDataManager = () => {
    // ensures BroadcastChannel is created only once on first render
    const channel = useMemo(() => new BroadcastChannel(storageName), []);

    // function to run management and remove expired item
    // it also return the next expiration time
    const runManagement = useCallback(() => {
        const items = storageObject.getItem(storageName);

        if (!items) {
            // skip it
            return null;
        }

        const parsedItems: PersistentData[] = JSON.parse(items);
        const now = new Date();
        const nextItems = parsedItems.filter(item => dayjs(item.expiresAt).isAfter(now));

        if (nextItems.length !== parsedItems.length) {
            // there was some expired items being processed
            // update the storage
            storageObject.setItem(storageName, JSON.stringify(nextItems));
            // broadcast message
            channel.postMessage(nextItems);
        }

        return nextItems.reduce<Dayjs | null>((acc, item) => {
            if (!acc) {
                return dayjs(item.expiresAt);
            }

            return dayjs(item.expiresAt).isBefore(acc) ? dayjs(item.expiresAt) : acc;
        }, null);
    }, [channel]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const run = () => {
            // first run the management and get next expiration
            const nextExpiration = runManagement();

            if (timeoutId) {
                // stop it
                // we have to remember run may be called outside timeout triggers
                clearTimeout(timeoutId);
            }

            timeoutId = null;

            if (nextExpiration !== null) {
                const timeout = nextExpiration.diff(new Date());
                timeoutId = setTimeout(() => {
                    timeoutId = null;
                    run();
                }, timeout);
            }
        };

        // subscribe to the channel
        channel.onmessage = event => run();

        // also run the management once right away
        run();

        return () => {
            if (timeoutId !== null) {
                // stop the timeout
                clearTimeout(timeoutId);
            }

            // properly close the channel
            channel.close();
        };
    }, [channel, runManagement]);

    // this component is not a context,
    // it simply needs to be mounted to initialize the storage lifecycle
    return null;
};

export default PersistentDataManager;

const generateSessionId = (sessionId: string) => (isNil(sessionId) ? nanoid() : sessionId);

export const usePersistentData = () => {
    const channel = useMemo(() => new BroadcastChannel(storageName), []);
    const isChannelOpen = useRef(true);

    // properly clean up with the component using this hook is unmounted
    useEffect(
        () => () => {
            channel.close();
            isChannelOpen.current = false;
        },
        [channel]
    );

    return useMemo(
        () => ({
            setItem: (key: string, data: any, intervalInSeconds: number, sessionId: string) => {
                // Add small overhead +2s, to ensure auto clean was not trigger before
                const expiresAt = dayjs()
                    .add(intervalInSeconds + 2, 's')
                    .toString();
                const item: PersistentData = {
                    payload: data,
                    expiresAt,
                    key,
                    sessionId: generateSessionId(sessionId),
                };

                const items = JSON.parse(storageObject.getItem(storageName) || '[]');
                const nextItems = [...items.filter((item: PersistentData) => item.key !== key), { ...item }];
                storageObject.setItem(storageName, JSON.stringify(nextItems));

                if (isChannelOpen.current) {
                    channel.postMessage(nextItems);
                }
            },
            getItem: <T = any>(key: string): T | null => {
                const items = JSON.parse(storageObject.getItem(storageName) || '[]');
                const item = items.find((item: PersistentData) => item.key === key);

                return item ? item.payload : null;
            },
            getSessionId: (key: string): string => {
                const items = JSON.parse(storageObject.getItem(storageName) || '[]');
                const item = items.find((item: PersistentData) => item.key === key);

                return generateSessionId(item?.sessionId);
            },
            deleteItem: (key: string) => {
                const items = JSON.parse(storageObject.getItem(storageName) || '[]');
                const filteredList = items.filter((item: PersistentData) => item.key !== key);
                storageObject.setItem(storageName, JSON.stringify(filteredList));

                if (isChannelOpen.current) {
                    channel.postMessage(filteredList);
                }
            },
        }),
        [channel]
    );
};

export const usePersistentDataListener = <Message>(itemKey: string) => {
    const [values, setValues] = useState<Message | null>(() => {
        const items = JSON.parse(storageObject.getItem(storageName) || '[]');
        const item = items.find((item: PersistentData) => item.key === itemKey);

        return item ? item.payload : null;
    });

    useEffect(() => {
        const channel = new BroadcastChannel(storageName);
        channel.onmessage = (allItems: PersistentData[]) => {
            const item = allItems.find((item: PersistentData) => item.key === itemKey);
            setValues(item ? item.payload : null);
        };

        return () => {
            channel.close();
        };
    }, [itemKey]);

    return values;
};

export const usePersistentCloseListener = (applicationId: string, onClose: () => void) => {
    const channelRef = useRef<BroadcastChannel | null>(null);

    const onCloseRef = useRef(onClose);

    useEffect(() => {
        channelRef.current = new BroadcastChannel(`${storageName}::${applicationId}`);
        channelRef.current.onmessage = () => {
            onCloseRef.current();
        };

        return () => {
            channelRef.current.close();
        };
    }, [applicationId]);

    return useCallback(() => channelRef.current?.postMessage('close'), []);
};
